import type { BasketBatch, BasketItem } from "../page";

const round3 = (n: number) => Math.round(n * 1000) / 1000;

// The line's batches; lines from older parked sales carry a single batch in the legacy fields
export const getLineBatches = (item: BasketItem): BasketBatch[] => {
  if (item.batches?.length) return item.batches;
  if (!item.batchId) return [];
  return [{
    batchId: item.batchId,
    batchName: item.batchName,
    expiryDate: item.batchExpiryDate,
    binInfo: item.batchBinInfo,
    available: item.stock,
    qty: item.qty,
  }];
};

// Total stock of the line's batches, or undefined if any batch's stock is unknown
export const getBatchesAvailable = (batches: BasketBatch[]): number | undefined =>
  batches.some((b) => b.available == null)
    ? undefined
    : round3(batches.reduce((sum, b) => sum + (b.available ?? 0), 0));

// Payload shapes match the ERP web POS (biznify-frontend-official pos-create-container)

export const buildItemSerials = (item: BasketItem) =>
  (item.serials ?? []).map((s) => ({
    serial: s.serial,
    serialId: s.serialId,
    binId: s.binId ?? null,
    warrantApplicable: true,
  }));

// A sale line that needs serials/a batch but doesn't have them (e.g. recalled from an older parked sale)
export const isBasketItemMissingTracking = (item: BasketItem): boolean => {
  if (item.isReturnLine || item.isBogoFreeItem || item.qty <= 0) return false;
  if (item.isSerialized) return (item.serials?.length ?? 0) !== item.qty;
  if (item.isBatchManaged) {
    const batches = getLineBatches(item);
    return !batches.length || round3(batches.reduce((sum, b) => sum + b.qty, 0)) !== round3(item.qty);
  }
  return false;
};

// One entry per batch per bin: each batch's qty is split across that batch's bins,
// in the order the API returns them
export const buildItemBatches = (item: BasketItem) => {
  const entries: { binId: number | null; batchId: number; quantity: number }[] = [];
  for (const batch of getLineBatches(item)) {
    let remaining = batch.qty;
    for (const bin of batch.binInfo ?? []) {
      if (remaining <= 0) break;
      if (bin.quantity <= 0) continue;
      const quantity = round3(Math.min(bin.quantity, remaining));
      entries.push({ binId: bin.binId, batchId: batch.batchId, quantity });
      remaining = round3(remaining - quantity);
    }
    // No bin breakdown (or it doesn't cover the qty) — send the remainder without a bin
    if (remaining > 0) entries.push({ binId: null, batchId: batch.batchId, quantity: remaining });
  }
  return entries;
};

export type ReturnLineBatch = { batchId: number; quantity: number };

// Returnable batches of an invoice line (invoice detail `returnBatchInfo`)
export const extractInvoiceLineBatches = (line: any): ReturnLineBatch[] =>
  (Array.isArray(line?.returnBatchInfo) ? line.returnBatchInfo : [])
    .map((b: any) => ({ batchId: Number(b?.batchId ?? 0), quantity: Number(b?.quantity ?? 0) }))
    .filter((b: ReturnLineBatch) => b.batchId > 0);

// Splits the returned quantity across the line's returnable batches, in the order the API returns them
export const buildReturnItemBatches = (batches: ReturnLineBatch[] | undefined, qty: number) => {
  if (!batches?.length || qty <= 0) return [];
  const entries: ReturnLineBatch[] = [];
  let remaining = qty;
  for (const b of batches) {
    if (remaining <= 0) break;
    if (b.quantity <= 0) continue;
    const quantity = Math.round(Math.min(b.quantity, remaining) * 1000) / 1000;
    entries.push({ batchId: b.batchId, quantity });
    remaining = Math.round((remaining - quantity) * 1000) / 1000;
  }
  // Batch quantities don't cover the return qty — put the remainder on the first batch
  if (remaining > 0) {
    if (entries.length) entries[0].quantity = Math.round((entries[0].quantity + remaining) * 1000) / 1000;
    else entries.push({ batchId: batches[0].batchId, quantity: remaining });
  }
  return entries;
};

// Serials for a return line: the first `qty` of the line's sold serials, skipping any already
// used by an earlier line of the same item (`used` is shared across the whole return)
export const buildReturnItemSerials = (
  serials: string[] | undefined,
  qty: number,
  used: Set<string> = new Set(),
) => {
  if (!serials?.length || qty <= 0) return [];
  const picked = serials.filter((s) => !used.has(s)).slice(0, Math.ceil(qty));
  picked.forEach((s) => used.add(s));
  return picked.map((serial) => ({ serial, warrantApplicable: true }));
};
